<template>
  <div class="page-edit-plan">
    <Overlay width="small" autoHeight :link="{name: 'plan'}">
      <Notification
        v-if="notification.active"
        :content="notification.content"
        :type="notification.type"
      />

      <transition name="fade" mode="out-in">
        <div key="cancel-plan" class="cancelling-plan" v-if="cancellingPlan">
          <h2 class="overlay-heading">Cancel your plan</h2>
          <img
            src="https://media2.giphy.com/media/eeL8EcBBTwSMLACw6F/200w.webp?cid=ecf05e47mp4txn6cbdngx4d9es3rlchhfl5pu5t67eicu984&rid=200w.webp"
          />

          <p>
            You're about to cancel this plan. This means that your website will
            won't be accessible to the public after <strong>{{ subscriptionEndDate }}</strong> and
            will be <strong>permanently deleted</strong> 30 days after.
          </p>

          <p>
            Please note that our friendly team would be more than happy to
            assist you with any issues that you may have had during your
            experience.
          </p>

          <p>
            As a small business, we endeavour to satisfy our customers – so any
            feedback, good or bad, is greatly appreciated.
          </p>

          <Btn
            subtle
            class="cancel-btn"
            @click="cancelPlan"
            :isLoading="btnCancelLoading">
            Cancel Plan
          </Btn>
        </div>

        <div key="change-plan" v-else>
          <h2 class="overlay-heading">Plan Update</h2>

          <p>
            When changing a plan, we will automatically prorate the next charge.
            This means that
            <strong>we'll only bill you for time used</strong> and
            <strong>apply credit for unused time</strong>.
          </p>

          <p>
            For example, if you're changing halfway through the billing period
            from the <strong>monthly</strong> to
            <strong>yearly</strong> subscription, you won't be charged the full
            yearly price.
          </p>

          <div class="plans">
            <div
              class="plan"
              :class="{'is-current': isCurrentPlan(monthlyPrice)}"
            >
              <h3>Monthly - £20/month</h3>
              <p>Stop and start anytime.</p>

              <Btn
                @click="initCancelPlan"
                subtle
                v-if="isCurrentPlan(monthlyPrice)"
              >
                Cancel Plan
              </Btn>
              <Btn
                tertiary
                v-else
                :isLoading="btnUpdateLoading"
                @click="handlePlanActivation(monthlyPrice)">
                Select Monthly
              </Btn>
            </div>

            <div
              class="plan"
              :class="{'is-current': isCurrentPlan(yearlyPrice)}"
            >
              <h3>Annually - £200/year</h3>
              <p>Get 2 months FREE!</p>

              <Btn
                @click="initCancelPlan"
                subtle
                v-if="isCurrentPlan(yearlyPrice)"
              >
                Cancel Plan
              </Btn>
              <Btn
                tertiary
                v-else
                :isLoading="btnUpdateLoading"
                @click="handlePlanActivation(yearlyPrice)">
                Select Yearly
              </Btn>
            </div>
          </div>
        </div>
      </transition>
    </Overlay>
  </div>
</template>

<script>
import moment from 'moment'
import {mapState} from 'vuex'

import {
  createSiteSubscriptionData,
  readSiteSubscriptionData,
  updateSiteSubscriptionData,
  cancelSiteSubscriptionData
} from '@/lib/payments/subscriptions'

import page from '@/mixins/page'
import notification from '@/mixins/notification'

import Overlay from '@/components/Overlay'
import Btn from '@/components/Btn'

export default {
  name: 'edit-plan',

  mixins: [page, notification],

  data() {
    return {
      title: 'Plan Info',
      subscription: {},
      cancellingPlan: false,
      btnCancelLoading: false,
      btnUpdateLoading: false,
      monthlyPrice: process.env.VUE_APP_STRIPE_MONTHLY_PRICE,
      yearlyPrice: process.env.VUE_APP_STRIPE_YEARLY_PRICE
    }
  },

  props: {
    siteId: String,
    companyId: String
  },

  components: {
    Overlay,
    Btn
  },

  computed: {
    ...mapState(['currentUser']),

    currentPlanId() {
      return this.subscription.items
        ? this.subscription.items.data[0].price.id
        : false
    },

    isTrialling() {
      return this.subscription ? this.subscription.status === 'trialing' : false
    },

    subscriptionEndDate() {
      return moment
        .unix(this.subscription.current_period_end)
        .format('dddd D MMMM - HH:MM')
    },

    subscriptionCancelled() {
      return this.subscription.status ? this.subscription.status === 'canceled' : ''
    },

    isCurrentPlan() {
      return (planId) => {
        return !this.subscriptionCancelled && planId === this.currentPlanId
      }
    }
  },

  methods: {
    getData() {
      readSiteSubscriptionData(this.companyId, this.siteId)
        .then(({data}) => {
          this.subscription = data
        })
        .catch(({response}) => {
          this.noActiveSubscription = true
        })
    },

    initCancelPlan() {
      this.cancellingPlan = true
    },

    cancelPlan() {
      this.btnCancelLoading = true

      cancelSiteSubscriptionData(this.siteId)
        .then(() => {
          this.triggerNotification('success', 'plan', 'cancelled')

          window.setTimeout(() => {
            this.cancellingPlan = false
            this.btnCancelLoading = false

            this.$router.push({name: 'plan', params: {siteId: this.siteId}})
          }, this.closeDelay)
        })
    },

    handlePlanActivation(price) {
      if (this.subscription.id) {
        this.updatePlan(price)
      } else {
        this.createSubscription(price)
      }
    },

    createSubscription(price) {
      let data = {
        price: price
      }

      this.btnUpdateLoading = true

      createSiteSubscriptionData(this.siteId, data)
        .then(({data}) => {
          this.triggerNotification('success', 'plan', 'activated', true)

          window.setTimeout(() => {
            this.btnUpdateLoading = false

            this.$router.push({
              name: 'invoice',
              params: {invoiceId: data.data.latest_invoice},
              query: {updatedSubscription: 'true'}
            })
          }, this.closeDelay)
        })
    },

    updatePlan(price) {
      let data = {
        current_price_id: this.currentPlanId,
        new_price_id: price
      }

      this.btnUpdateLoading = true

      updateSiteSubscriptionData(this.siteId, data)
        .then(({data}) => {
          this.triggerNotification('success', 'plan', 'updated', true)
          this.btnUpdateLoading = false

          window.setTimeout(() => {
            if (this.isTrialling) {
              this.$router.push({
                name: 'plan',
                params: {siteId: this.siteId}
              })
            } else {
              this.$router.push({
                name: 'invoice',
                params: {invoiceId: data.latest_invoice},
                query: {updatedSubscription: 'true'}
              })
            }
          }, this.closeDelay)
        })
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass" scoped>
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

.plans
  display: flex
  align-items: center

.plan
  border: 1px solid $color-border
  margin: $gap-medium $gap-small
  padding: $gap-medium $gap-small
  width: 50%
  border-radius: 3px
  text-align: center
  position: relative

  &.is-current
    background: rgba(0, 0, 0, 0.05)

    &:before
      content: 'Current Plan'
      position: absolute
      top: $gap-small
      left: 0
      text-align: center
      font-size: 9px
      width: 100%

.cancel-btn
  margin: $gap-medium

.cancelling-plan
  text-align: center

  .btn
    margin: $gap-small $gap-x-small 0

  img
    margin: $gap-large auto
    width: 300px
    border-radius: 5px
</style>
